import { useCallback, useMemo } from "react";

import {
  saveTransportGroup,
  deleteTransportGroup,
  updateTransportGroupPassengers,
  getTransportErrorMessage,
} from "../../Transport/api/transportBoardApi";
import {
  buildFilteredJourneys,
  buildSelectedCampusLabel,
  buildAdditionalPassengersForBus,
  limitPassengersToCapacity,
  buildPassengerPayload,
  buildJourneyUpdatePayload,
} from "../../Transport/utils/transportBoardUtils";
import useTransportGroupsByDate from "../../Transport/hooks/useTransportGroupsByDate";
import { useBusDialog } from "../../Transport/hooks/useBusDialog";
import { useAirports } from "../../Transfers/hooks/useAirports";

/**
 * Owns the transport "Journeys" data + handlers for the Activities board so the
 * journeys column (column 3) and the activity-group column (column 2) can share
 * a single source of truth. This makes it possible to assign activity-group
 * members straight onto a bus without leaving the Activities view.
 *
 * Reuses the Transport module's API, capacity/dedup helpers and bus dialog so
 * behaviour matches the dedicated Transport view exactly.
 *
 * Pass `enabled: false` to skip fetching journeys while the transport column is
 * hidden.
 */
export default function useActivityTransport({
  selectedDate,
  selectedCampusId,
  campusList = [],
  showToast,
  enabled = true,
}) {
  const {
    groups: journeys = [],
    loading,
    error,
    refetch,
  } = useTransportGroupsByDate(selectedDate, { enabled });

  const { airports } = useAirports({ activeOnly: true });

  const campDateStr = useMemo(
    () => (selectedDate ? selectedDate.format("YYYY-MM-DD") : null),
    [selectedDate]
  );

  const {
    busDialogOpen,
    editingJourney,
    openAddBus,
    openEditBus,
    closeBusDialog,
  } = useBusDialog();

  const filteredJourneys = useMemo(
    () => buildFilteredJourneys({ journeys, campusList, selectedCampusId }),
    [journeys, campusList, selectedCampusId]
  );

  const selectedCampusLabel = useMemo(
    () => buildSelectedCampusLabel({ selectedCampusId, campusList }),
    [selectedCampusId, campusList]
  );

  const refreshJourneys = useCallback(async () => {
    await refetch?.(selectedDate);
  }, [refetch, selectedDate]);

  const handleSaveBus = async ({ mode, id, payload }) => {
    try {
      const result = await saveTransportGroup({ mode, id, payload });
      showToast?.(
        mode === "add"
          ? "Bus created"
          : result?.confirmed
          ? "Bus confirmed"
          : result?.unconfirmed
          ? "Bus unconfirmed"
          : "Bus updated",
        "success"
      );
      closeBusDialog();
      await refreshJourneys();
      return mode === "add" ? result : true;
    } catch (e) {
      console.error("Save bus failed:", e);
      showToast?.(getTransportErrorMessage(e, "Save failed"), "error");
      throw e;
    }
  };

  // Create a ticket block — a transport group of journeyType TICKET. Reuses the
  // same create endpoint as a bus; the caller builds the (stops-free) payload.
  const createTicketBlock = async (payload) => {
    try {
      await saveTransportGroup({ mode: "add", payload });
      showToast?.("Ticket block created", "success");
      await refreshJourneys();
    } catch (e) {
      console.error("Create ticket block failed:", e);
      showToast?.(getTransportErrorMessage(e, "Save failed"), "error");
      throw e;
    }
  };

  const handleDeleteJourney = async (journey) => {
    try {
      await deleteTransportGroup(journey.id);
      showToast?.("Bus deleted", "success");
      await refreshJourneys();
    } catch (e) {
      showToast?.(getTransportErrorMessage(e, "Delete failed"), "error");
    }
  };

  const savePassengersForBus = async (busId, nextPassengers) => {
    try {
      await updateTransportGroupPassengers(busId, nextPassengers);
      await refreshJourneys();
    } catch (e) {
      showToast?.(getTransportErrorMessage(e, "Update failed"), "error");
    }
  };

  const confirmJourney = async (journey, { contactName, contactNumber }) => {
    try {
      await saveTransportGroup({
        mode: "edit",
        id: journey.id,
        payload: buildJourneyUpdatePayload(journey, {
          contactName,
          contactNumber,
          confirm: true,
        }),
      });
      showToast?.("Bus confirmed", "success");
      await refreshJourneys();
    } catch (e) {
      showToast?.(getTransportErrorMessage(e, "Confirm failed"), "error");
      throw e;
    }
  };

  // Assign a set of activity-group members onto a bus. Activity members are
  // shaped { personType, personId, name }; the transport helpers expect an `id`,
  // so we map personId -> id and lean on the shared capacity/dedup helpers.
  const assignMembersToBus = async ({ busId, members }) => {
    if (!Array.isArray(members) || members.length === 0) {
      showToast?.("No members to assign.", "info");
      return;
    }

    const bus = filteredJourneys.find((j) => Number(j.id) === Number(busId));
    if (!bus) {
      showToast?.("Bus not found.", "error");
      return;
    }

    const people = members.map((m) => ({
      personType: String(m.personType).toUpperCase(),
      id: m.personId ?? m.id,
    }));

    const toAdd = buildAdditionalPassengersForBus({ bus, missingPeople: people });
    const { addNow } = limitPassengersToCapacity({ bus, passengersToAdd: toAdd });

    if (addNow.length === 0) {
      showToast?.(
        "Nobody added — everyone is already on this bus, or it is full.",
        "info"
      );
      return;
    }

    const existing = Array.isArray(bus.passengers) ? bus.passengers : [];

    try {
      await updateTransportGroupPassengers(busId, [
        ...buildPassengerPayload(existing),
        ...addNow,
      ]);

      const notAdded = people.length - addNow.length;
      if (notAdded > 0) {
        showToast?.(
          `Assigned ${addNow.length} member(s). ${notAdded} skipped (already aboard or no capacity).`,
          "warning"
        );
      } else {
        showToast?.(`Assigned ${addNow.length} member(s) to bus.`, "success");
      }

      await refreshJourneys();
    } catch (e) {
      showToast?.(getTransportErrorMessage(e, "Assign failed"), "error");
    }
  };

  return {
    journeys: filteredJourneys,
    loading,
    error,
    campDateStr,
    selectedCampusLabel,
    airports,
    busDialogOpen,
    editingJourney,
    openAddBus,
    openEditBus,
    closeBusDialog,
    handleSaveBus,
    createTicketBlock,
    handleDeleteJourney,
    savePassengersForBus,
    confirmJourney,
    assignMembersToBus,
    refreshJourneys,
  };
}
